import React, { useState } from "react";
import Image from "../../../components/elements/Image";
import Input from "../../../components/form/Input";
import Button from "../../../components/forms/Button";
import { classNames } from "../../../helpers/classNames";
import Logo from "../../../assets/images/logo-memory-doodle-stack.svg";
import GoogleLogo from "../../../assets/image/google.svg";
import FacebookLogo from "../../../assets/image/facebook.svg";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  authLogin,
  resendVerificationEmail,
} from "../../../services/authService";
import { toast } from "react-toastify";
import { apiRequest } from "../../../util/apiUtils";
import { login } from "../../../redux/slice/authSlice";
import useMutation from "../../../hooks/useMutation";
import LoadingFallback from "../../../components/elements/LoadingFallback";

const Signin = () => {
  const socialData = [
    { _id: 1, name: "Facebook", logo: FacebookLogo },
    { _id: 2, name: "Google", logo: GoogleLogo },
  ];
  const [form, setForm] = useState({ email: "", password: "" });
  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);

  const { mutate: resendVerify, isPending: isResending } = useMutation({
    mutationFn: resendVerificationEmail,
    onSuccess: (res) => {
      if (res?.status === 200) {
        toast.success("Verification email sent!");
      } else {
        toast.error(res?.message || "Failed to send verification email");
      }
    },
    onError: () => toast.error("Something went wrong"),
  });

  const handleLogin = () => {
    if (loading) return;
    setLoading(true);
    dispatch(
      login({
        email: form.email,
        password: form.password,
        type: "user",
        redirect: "/dashboard",
        navigate,
        location,
      })
    ).finally(() => setLoading(false));
  };

  const handleShopifyLogin = () => {
    const state = Math.random().toString(36).substring(7);
    sessionStorage.setItem('shopify_oauth_state', state);
    
    const shopifyAuthUrl = 
      `https://${process.env.REACT_APP_SHOPIFY_STORE}.myshopify.com/admin/oauth/authorize?client_id=${process.env.REACT_APP_SHOPIFY_OAUTH_ACCESS_TOKEN}&scope=${encodeURIComponent(process.env.REACT_APP_SHOPIFY_OAUTH_SCOPE)}&redirect_uri=${encodeURIComponent(process.env.REACT_APP_SHOPIFY_REDIRECT_URI)}&state=${state}&grant_options[]=per-user`;
    
    window.location.href = shopifyAuthUrl;
  };

  return <LoadingFallback />

  return (
    <>
      <section className="relative min-h-[calc(100vh-6rem)] py-10 flex items-center overflow-hidden bg-md-pink-light-2">
        <div className="relative w-full px-4 sm:px-0 sm:max-w-xl md:max-w-2xl lg:max-w-4xl xl:max-w-6xl 2xl:max-w-7xl 3xl:max-w-8xl mx-auto">
          <div className="w-full max-w-lg mx-auto space-y-8">
            <div className="h-36">
              <Image
                src={Logo}
                alt={""}
                width={"100%"}
                height={"100%"}
                effect={"blur"}
                classes={"object-contain"}
              />
            </div>
            <div className="relative bg-white shadow-md shadow-md-pink/10 py-6 px-6 rounded-xl space-y-6">
              <h2 className="text-2xl font-semibold text-md-dark text-center">
                Signin to your account
              </h2>
              <div className="space-y-4">
                <div className="relative">
                  <Input
                    inputPlaceholder={"Enter your email address"}
                    inputClasses={""}
                    labelClasses={""}
                    label={"Email"}
                    inputType={"email"}
                    inputName="email"
                    inputValue={form.email}
                    onChange={handleChange}
                  />
                </div>
                <div className="relative">
                  <Input
                    inputPlaceholder={"Enter your password"}
                    inputClasses={""}
                    labelClasses={""}
                    label={"Password"}
                    inputType={"password"}
                    inputName="password"
                    inputValue={form.password}
                    onChange={handleChange}
                  />
                </div>
                <div className="relative !mt-8">
                  <Button
                    buttonLabel={loading ? "Signing in..." : "Sign In"}
                    isDisable={loading}
                    buttonClasses="!rounded-md !pb-1 w-full"
                    buttonLabelClasses="!text-xl font-medium"
                    buttonFunction={handleLogin}
                  />
                </div>
                <div className="relative !mt-4">
                  <Button
                    buttonLabel="Sign In with Shopify"
                    buttonClasses="!rounded-md !pb-1 w-full !bg-green-600 hover:!bg-green-700"
                    buttonLabelClasses="!text-xl font-medium"
                    buttonFunction={handleShopifyLogin}
                  />
                </div>
                <div className="relative flex justify-center">
                  <Button
                    buttonHasLink={true}
                    buttonLink={"/forgot-password"}
                    buttonLabel={"Forgot your password?"}
                    buttonLabelClasses={"font-medium"}
                    buttonClasses={
                      "!h-auto !px-0 !rounded-none !bg-transparent !text-md-pink hover:underline"
                    }
                  />
                </div>
                <div className="relative flex justify-center mt-2">
                  <Button
                    buttonLabel={isResending ? "Sending..." : "Reverify Email"}
                    isDisable={!form.email || isResending}
                    buttonFunction={() => resendVerify({ email: form.email })}
                    buttonClasses="!h-auto !px-0 !rounded-none !bg-transparent !text-md-pink hover:underline text-sm"
                  />
                </div>
              </div>
              <div className="overflow-hidden flex items-center justify-center relative">
                <span
                  className={classNames(
                    "relative inline-flex items-center justify-center bg-transparent text-slate-400 capitalize",
                    "before:content-[''] before:absolute before:top-1/2 before:left-[calc(100%+12px)] before:w-[400px] before:border-t before:border-slate-200 before:mt-px",
                    "after:content-[''] after:absolute after:top-1/2 after:right-[calc(100%+12px)] after:w-[400px] after:border-t after:border-slate-200 after:mt-px"
                  )}
                >
                  or
                </span>
              </div>
              {socialData?.length > 0 && (
                <div className="flex items-center justify-center gap-2">
                  {socialData.map((item, index) => (
                    <Button
                      key={item._id}
                      buttonClasses="!bg-transparent !rounded-md !p-2 !h-auto border border-slate-200 w-full hover:!bg-md-pink-light-2 hover:!border-md-pink-light-1"
                      buttonLabel={
                        <div className="flex items-center gap-1 md:gap-2">
                          <div className="w-8 aspect-square overflow-hidden">
                            <Image
                              src={item.logo}
                              alt={item.name}
                              effect={"blur"}
                              classes={"object-contain"}
                            />
                          </div>
                          <div className="text-md-dark text-base font-medium tracking-wide">
                            {item.name}
                          </div>
                        </div>
                      }
                    />
                  ))}
                </div>
              )}
              <div className="text-center flex items-center justify-center text-base text-slate-400">
                <span>Don't have an account?</span>
                <Button
                  buttonLabel={"Sign Up"}
                  buttonLabelClasses={"!font-semibold"}
                  buttonClasses={
                    "!h-6 !px-1 !bg-white !text-md-pink !rounded-none hover:underline"
                  }
                  buttonHasLink={true}
                  buttonLink={"/signup"}
                />
                <span>here</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Signin;